/**
 * URLs for the Flash Hotel Rewards area, which lives as a sub-tab of the loyalty Offers page
 * (`/loyalty/societe/offers?tab=flash-rewards`). The Offers page owns `?tab=`, so Flash Rewards'
 * own sub-tabs use `?flashTab=`.
 */

/** Flash Rewards sub-tabs, in display order. */
export const FLASH_REWARDS_SUB_TABS = ['offers', 'overrides', 'pools', 'reservations', 'config'] as const;

export type FlashRewardsSubTab = (typeof FLASH_REWARDS_SUB_TABS)[number];

/** Query param holding the active Flash Rewards sub-tab. */
export const FLASH_TAB_PARAM = 'flashTab';

/** Base URL of the Flash Rewards sub-tab on the loyalty Offers page. */
const FLASH_REWARDS_BASE = '/loyalty/societe/offers?tab=flash-rewards';

/** Reads a `flashTab` value, defaulting to `offers` for a missing or unknown one. */
export function flashRewardsSubTabFrom(value: string | null): FlashRewardsSubTab {
  return FLASH_REWARDS_SUB_TABS.includes(value as FlashRewardsSubTab) ? (value as FlashRewardsSubTab) : 'offers';
}

/** Deep link to one offer's Société rewards in the Flash Rewards Offers sub-tab. */
export function flashRewardsOfferUrl(offerId: string): string {
  const params = new URLSearchParams({ [FLASH_TAB_PARAM]: 'offers', offerId });
  return `${FLASH_REWARDS_BASE}&${params.toString()}`;
}

/**
 * Where an old `/loyalty/societe/flash-rewards` link should go now. The old page used `?tab=` for
 * its own sub-tabs, so a known value moves to `flashTab`; an unknown one is dropped. Every other
 * param (e.g. `offerId`) is kept.
 */
export function legacyFlashRewardsRedirectUrl(search: string): string {
  const oldParams = new URLSearchParams(search);
  const oldTab = oldParams.get('tab');
  oldParams.delete('tab');
  const params = new URLSearchParams();
  if (oldTab && FLASH_REWARDS_SUB_TABS.includes(oldTab as FlashRewardsSubTab)) {
    params.set(FLASH_TAB_PARAM, oldTab);
  }
  oldParams.forEach((value, key) => params.append(key, value));
  const rest = params.toString();
  return rest ? `${FLASH_REWARDS_BASE}&${rest}` : FLASH_REWARDS_BASE;
}

/**
 * The query string with `offerId` set (or removed when null), or null when it already matches.
 * Callers skip the URL write on null. Writing an unchanged URL isn't harmless here: React
 * Router's functional `setSearchParams` updater isn't queued, so a write made while another
 * navigation is in flight can restore a stale `?tab=` and bounce the Offers page.
 */
export function withOfferIdParam(current: URLSearchParams, offerId: string | null): URLSearchParams | null {
  if ((current.get('offerId') ?? null) === offerId) {
    return null;
  }
  const next = new URLSearchParams(current);
  if (offerId) {
    next.set('offerId', offerId);
  } else {
    next.delete('offerId');
  }
  return next;
}
